(() => {
  const HUB_SELECTOR = '[data-gvs-product-tabs]';
  const MOBILE_LABELS = {
    details: 'Details',
    included: 'Included',
    compatibility: 'Compatibility',
    faqs: 'FAQs',
    reviews: 'Reviews',
  };

  const RUNTIME_KEY = '__gvsProductTabsRuntime';

  window[RUNTIME_KEY]?.destroy?.();

  const runtimeController = new AbortController();
  const hubStates = new Map();

  const normaliseHash = (value) =>
    String(value || '')
      .replace(/^#/, '')
      .trim();

  const safeQuery = (
    selector,
    scope = document
  ) => {
    if (!selector) return null;

    try {
      return scope.querySelector(selector);
    } catch (error) {
      console.warn(
        'GVS product tabs: invalid selector',
        selector,
        error
      );

      return null;
    }
  };

  const getSourceWrapper = (node) => {
    if (!node) return null;

    return node.closest(
      '.shopify-section'
    );
  };

  const markSourceWrapperMoved = (
    wrapper
  ) => {
    if (!wrapper) return;

    wrapper.classList.add(
      'gvs-product-tabs__source-wrapper--moved'
    );

    wrapper.setAttribute(
      'aria-hidden',
      'true'
    );
  };

  const restoreSourceWrapper = (
    wrapper
  ) => {
    if (!wrapper) return;

    wrapper.classList.remove(
      'gvs-product-tabs__source-wrapper--moved'
    );

    wrapper.removeAttribute(
      'aria-hidden'
    );
  };

  const restoreNodePlacement = (
    node,
    parent,
    nextSibling
  ) => {
    if (!node || !parent) return;

    const reference =
      nextSibling &&
      nextSibling.parentNode === parent
        ? nextSibling
        : null;

    if (
      node.parentNode !== parent ||
      node.nextSibling !== reference
    ) {
      parent.insertBefore(
        node,
        reference
      );
    }
  };

  const getPanelCount = (
    key,
    panel
  ) => {
    if (!panel) return 0;

    if (key === 'included') {
      const suppliedCount =
        Number.parseInt(
          panel.querySelector(
            '.gvs-whats-included__count-number'
          )?.textContent || '',
          10
        );

      if (
        Number.isFinite(
          suppliedCount
        ) &&
        suppliedCount > 0
      ) {
        return suppliedCount;
      }

      return panel.querySelectorAll(
        '.gvs-whats-included__item'
      ).length;
    }

    if (key === 'compatibility') {
      return panel.querySelectorAll(
        '.gvs-compatibility__card, .gvs-compatibility__note'
      ).length;
    }

    if (key === 'faqs') {
      return panel.querySelectorAll(
        '.product-faqs__item'
      ).length;
    }

    if (key === 'reviews') {
      const reviewCount =
        Number.parseInt(
          panel.dataset.reviewCount || '',
          10
        );

      if (
        Number.isFinite(reviewCount) &&
        reviewCount > 0
      ) {
        return reviewCount;
      }

      return 0;
    }

    return 0;
  };

  const getTabCountLabel = (
    key,
    count
  ) => {
    if (key === 'reviews') {
      return `${count} ${
        count === 1
          ? 'review'
          : 'reviews'
      }`;
    }

    if (key === 'faqs') {
      return `${count} ${
        count === 1
          ? 'question'
          : 'questions'
      }`;
    }

    if (key === 'included') {
      return `${count} included ${
        count === 1
          ? 'item'
          : 'items'
      }`;
    }

    if (key === 'compatibility') {
      return `${count} compatibility ${
        count === 1
          ? 'item'
          : 'items'
      }`;
    }

    return `${count} ${
      count === 1
        ? 'item'
        : 'items'
    }`;
  };

  const panelHasContent = (panel) => {
    if (!panel) return false;

    const meaningfulElement =
      panel.querySelector(
        ':scope > *:not(script):not(style), ' +
          ':scope > .gvs-product-tabs__review-content > *:not(script):not(style)'
      );

    return Boolean(
      meaningfulElement ||
        panel.textContent.trim()
    );
  };

  const getDeclaredReviewCount = (
    node
  ) => {
    if (!node) return null;

    const countNodes = [];

    if (
      node.matches?.(
        '[data-number-of-reviews]'
      )
    ) {
      countNodes.push(node);
    }

    countNodes.push(
      ...node.querySelectorAll(
        '[data-number-of-reviews]'
      )
    );

    const counts = countNodes
      .map((countNode) =>
        Number.parseInt(
          countNode.getAttribute(
            'data-number-of-reviews'
          ) || '',
          10
        )
      )
      .filter(Number.isFinite);

    return counts.length > 0
      ? Math.max(...counts)
      : null;
  };

  const extractReviewContent = (
    hub,
    detailsSource
  ) => {
    const reviewSelector =
      hub.dataset.reviewsSelector;

    if (!reviewSelector) {
      return {
        node: null,
        restore: null,
      };
    }

    const reviewSource = safeQuery(
      reviewSelector,
      detailsSource || document
    );

    if (
      !reviewSource ||
      hub.contains(reviewSource)
    ) {
      return {
        node: null,
        restore: null,
      };
    }

    const originalParent =
      reviewSource.parentNode;

    const originalNextSibling =
      reviewSource.nextSibling;

    const restoreReviewSource = () => {
      restoreNodePlacement(
        reviewSource,
        originalParent,
        originalNextSibling
      );
    };

    if (
      getDeclaredReviewCount(
        reviewSource
      ) === 0
    ) {
      reviewSource.remove();

      return {
        node: null,
        restore: restoreReviewSource,
      };
    }

    const content =
      reviewSource.querySelector(
        '.gvs-faq-accordion__content-inner'
      ) ||
      reviewSource.querySelector(
        '.details-content'
      ) ||
      reviewSource;

    const reviewContainer =
      document.createElement('div');

    reviewContainer.className =
      'gvs-product-tabs__review-content';

    while (content.firstChild) {
      reviewContainer.appendChild(
        content.firstChild
      );
    }

    if (reviewSource !== content) {
      reviewSource.remove();
    }

    const restore = () => {
      while (reviewContainer.firstChild) {
        content.appendChild(
          reviewContainer.firstChild
        );
      }

      if (reviewSource !== content) {
        restoreReviewSource();
      }
    };

    return {
      node: reviewContainer,
      restore,
    };
  };

  const restoreHubSources = (
    state
  ) => {
    if (!state) return;

    if (
      typeof state.reviewRestore ===
      'function'
    ) {
      state.reviewRestore();
      state.reviewRestore = null;
    }

    for (
      let index =
        state.movedSources.length - 1;
      index >= 0;
      index -= 1
    ) {
      const record =
        state.movedSources[index];

      restoreNodePlacement(
        record.node,
        record.parent,
        record.nextSibling
      );

      if (record.hideWrapper) {
        restoreSourceWrapper(
          record.wrapper
        );
      }
    }

    state.movedSources.length = 0;
  };

  const resetHubShell = (
    hub
  ) => {
    const tabList = hub.querySelector(
      '[data-gvs-tabs-list]'
    );

    if (tabList) {
      tabList.replaceChildren();
      tabList.hidden = false;

      tabList.classList.remove(
        'is-scrollable',
        'is-at-start',
        'is-at-end'
      );
    }

    hub
      .querySelectorAll(
        '[data-gvs-tab-panel]'
      )
      .forEach((panel) => {
        panel.hidden = true;

        panel.classList.remove(
          'is-active',
          'is-entering'
        );

        panel.removeAttribute(
          'aria-labelledby'
        );
      });

    hub.classList.remove(
      'is-ready',
      'is-fallback'
    );
  };

  const cleanupHub = (
    hub,
    options = {}
  ) => {
    const {
      restoreSources = false,
    } = options;

    const state =
      hubStates.get(hub);

    if (!state) {
      if (restoreSources) {
        resetHubShell(hub);
      }

      return;
    }

    state.controller?.abort();
    state.resizeObserver?.disconnect();

    if (
      state.tabListFrame !== null
    ) {
      window.cancelAnimationFrame(
        state.tabListFrame
      );

      state.tabListFrame = null;
    }

    if (restoreSources) {
      restoreHubSources(state);
      resetHubShell(hub);

      delete hub.dataset
        .gvsTabsInitialised;
    }

    hubStates.delete(hub);
  };

  const failOpenHub = (
    hub,
    state,
    error
  ) => {
    console.error(
      'GVS product tabs: initialisation failed. Original product information has been restored.',
      error
    );

    state?.controller?.abort();
    state?.resizeObserver?.disconnect();

    if (
      state &&
      state.tabListFrame !== null
    ) {
      window.cancelAnimationFrame(
        state.tabListFrame
      );

      state.tabListFrame = null;
    }

    restoreHubSources(state);
    resetHubShell(hub);

    hub.dataset.gvsTabsInitialised =
      'failed';

    hubStates.delete(hub);
  };

  const createTabButton = (
    hub,
    panel,
    key,
    index
  ) => {
    const button =
      document.createElement(
        'button'
      );

    const label =
      panel.dataset.label || key;

    const buttonId =
      `GvsProductTab-${
        hub.id || 'hub'
      }-${key}`;

    button.type = 'button';
    button.id = buttonId;

    button.className =
      'gvs-product-tabs__tab';

    button.setAttribute(
      'role',
      'tab'
    );

    button.setAttribute(
      'aria-controls',
      panel.id
    );

    button.setAttribute(
      'aria-selected',
      'false'
    );

    button.setAttribute(
      'tabindex',
      index === 0 ? '0' : '-1'
    );

    button.dataset.gvsTabTarget =
      key;

    const desktopLabel =
      document.createElement(
        'span'
      );

    desktopLabel.className =
      'gvs-product-tabs__tab-label ' +
      'gvs-product-tabs__tab-label--desktop';

    desktopLabel.textContent =
      label;

    button.appendChild(
      desktopLabel
    );

    const mobileLabel =
      document.createElement(
        'span'
      );

    mobileLabel.className =
      'gvs-product-tabs__tab-label ' +
      'gvs-product-tabs__tab-label--mobile';

    mobileLabel.textContent =
      MOBILE_LABELS[key] ||
      label;

    button.appendChild(
      mobileLabel
    );

    button.setAttribute(
      'aria-label',
      label
    );

    if (
      hub.dataset.showCounts ===
      'true'
    ) {
      const count =
        getPanelCount(
          key,
          panel
        );

      if (count > 0) {
        const countLabel =
          getTabCountLabel(
            key,
            count
          );

        const countElement =
          document.createElement(
            'span'
          );

        countElement.className =
          'gvs-product-tabs__tab-count';

        countElement.textContent =
          String(count);

        countElement.setAttribute(
          'aria-hidden',
          'true'
        );

        button.setAttribute(
          'aria-label',
          `${label}, ${countLabel}`
        );

        button.appendChild(
          countElement
        );
      }
    }

    panel.setAttribute(
      'aria-labelledby',
      buttonId
    );

    return button;
  };

  const initialiseHub = (
    hub
  ) => {
    if (
      hub.dataset
        .gvsTabsInitialised ===
      'true'
    ) {
      return;
    }

    cleanupHub(hub, {
      restoreSources: true,
    });

    const state = {
      controller:
        new AbortController(),
      resizeObserver: null,
      tabListFrame: null,
      movedSources: [],
      reviewRestore: null,
    };

    hubStates.set(
      hub,
      state
    );

    try {
      const panelContainer =
        hub.querySelector(
          '[data-gvs-tabs-panels]'
        );

      const tabList =
        hub.querySelector(
          '[data-gvs-tabs-list]'
        );

      if (
        !panelContainer ||
        !tabList
      ) {
        throw new Error(
          'Product information tab shell is incomplete.'
        );
      }

      const { signal } =
        state.controller;

      const panels = new Map(
        Array.from(
          panelContainer
            .querySelectorAll(
              '[data-gvs-tab-panel]'
            )
        ).map((panel) => [
          panel.dataset
            .gvsTabPanel,
          panel,
        ])
      );

      const detailsSource =
        safeQuery(
          hub.dataset
            .detailsSelector
        );

      const reviewExtraction =
        extractReviewContent(
          hub,
          detailsSource
        );

      state.reviewRestore =
        reviewExtraction.restore;

      const sources = [
        {
          key: 'details',
          node: detailsSource,
          hideWrapper: true,
          trackRestore: true,
        },
        {
          key: 'included',
          node: safeQuery(
            hub.dataset
              .includedSelector
          ),
          hideWrapper: true,
          trackRestore: true,
        },
        {
          key: 'compatibility',
          node: safeQuery(
            hub.dataset
              .compatibilitySelector
          ),
          hideWrapper: true,
          trackRestore: true,
        },
        {
          key: 'faqs',
          node: safeQuery(
            hub.dataset
              .faqsSelector
          ),
          hideWrapper: false,
          trackRestore: true,
        },
        {
          key: 'reviews',
          node:
            reviewExtraction.node,
          hideWrapper: false,
          trackRestore: false,
        },
      ];

      sources.forEach(
        ({
          key,
          node,
          hideWrapper,
          trackRestore,
        }) => {
          const panel =
            panels.get(key);

          if (
            !panel ||
            !node ||
            hub.contains(node)
          ) {
            return;
          }

          if (trackRestore) {
            const wrapper =
              getSourceWrapper(
                node
              );

            state.movedSources.push(
              {
                node,
                parent:
                  node.parentNode,
                nextSibling:
                  node.nextSibling,
                wrapper,
                hideWrapper,
              }
            );

            if (hideWrapper) {
              markSourceWrapperMoved(
                wrapper
              );
            }
          }

          panel.appendChild(
            node
          );
        }
      );

      const availablePanels =
        sources
          .map(({ key }) =>
            panels.get(key)
          )
          .filter((panel) =>
            panelHasContent(
              panel
            )
          );

      panels.forEach(
        (panel) => {
          if (
            !availablePanels.includes(
              panel
            )
          ) {
            panel.remove();
          }
        }
      );

      if (
        availablePanels.length ===
        0
      ) {
        throw new Error(
          'No product information panels contain content.'
        );
      }

      const buttons =
        availablePanels.map(
          (
            panel,
            index
          ) => {
            const key =
              panel.dataset
                .gvsTabPanel;

            const button =
              createTabButton(
                hub,
                panel,
                key,
                index
              );

            tabList.appendChild(
              button
            );

            return button;
          }
        );

      const handleRuntimeError =
        (error) => {
          failOpenHub(
            hub,
            state,
            error
          );
        };

      const runSafely =
        (callback) =>
        (...args) => {
          if (
            signal.aborted
          ) {
            return undefined;
          }

          try {
            return callback(
              ...args
            );
          } catch (error) {
            handleRuntimeError(
              error
            );

            return undefined;
          }
        };

      const updateTabListState =
        () => {
          state.tabListFrame =
            null;

          const maximumScroll =
            Math.max(
              0,
              tabList.scrollWidth -
                tabList.clientWidth
            );

          const isScrollable =
            maximumScroll > 2;

          tabList.classList.toggle(
            'is-scrollable',
            isScrollable
          );

          tabList.classList.toggle(
            'is-at-start',
            !isScrollable ||
              tabList.scrollLeft <=
                2
          );

          tabList.classList.toggle(
            'is-at-end',
            !isScrollable ||
              tabList.scrollLeft >=
                maximumScroll - 2
          );
        };

      const requestTabListUpdate =
        () => {
          if (
            state.tabListFrame !==
            null
          ) {
            return;
          }

          state.tabListFrame =
            window.requestAnimationFrame(
              runSafely(
                updateTabListState
              )
            );
        };

      tabList.addEventListener(
        'scroll',
        requestTabListUpdate,
        {
          passive: true,
          signal,
        }
      );

      if (
        'ResizeObserver' in
        window
      ) {
        state.resizeObserver =
          new ResizeObserver(
            runSafely(
              requestTabListUpdate
            )
          );

        state.resizeObserver.observe(
          tabList
        );
      } else {
        window.addEventListener(
          'resize',
          requestTabListUpdate,
          {
            passive: true,
            signal,
          }
        );
      }

      if (
        availablePanels.length ===
          1 &&
        hub.dataset
          .hideSingleTabs ===
          'true'
      ) {
        tabList.hidden = true;
      }

      const scrollPanelIntoView =
        (panel) => {
          if (
            !panel ||
            !window
              .matchMedia(
                '(max-width: 749px)'
              )
              .matches
          ) {
            return;
          }

          window.requestAnimationFrame(
            runSafely(() => {
              window.requestAnimationFrame(
                runSafely(() => {
                  const rootStyles =
                    window.getComputedStyle(
                      document
                        .documentElement
                    );

                  const headerHeight =
                    Number.parseFloat(
                      rootStyles
                        .getPropertyValue(
                          '--header-height'
                        )
                    ) || 0;

                  const navHeight =
                    tabList
                      .getBoundingClientRect()
                      .height || 0;

                  const panelTop =
                    window.scrollY +
                    panel
                      .getBoundingClientRect()
                      .top;

                  const targetTop =
                    Math.max(
                      0,
                      panelTop -
                        headerHeight -
                        navHeight -
                        10
                    );

                  const reducedMotion =
                    window
                      .matchMedia(
                        '(prefers-reduced-motion: reduce)'
                      )
                      .matches;

                  window.scrollTo({
                    top:
                      targetTop,
                    behavior:
                      reducedMotion
                        ? 'auto'
                        : 'smooth',
                  });
                })
              );
            })
          );
        };

      const activate = (
        key,
        options = {}
      ) => {
        const targetPanel =
          panels.get(key);

        const targetButton =
          buttons.find(
            (button) =>
              button.dataset
                .gvsTabTarget ===
              key
          );

        if (
          !targetPanel ||
          !targetButton ||
          !availablePanels.includes(
            targetPanel
          )
        ) {
          return;
        }

        buttons.forEach(
          (button) => {
            const isActive =
              button ===
              targetButton;

            button.setAttribute(
              'aria-selected',
              String(isActive)
            );

            button.setAttribute(
              'tabindex',
              isActive
                ? '0'
                : '-1'
            );
          }
        );

        availablePanels.forEach(
          (panel) => {
            const isActive =
              panel ===
              targetPanel;

            panel.hidden =
              !isActive;

            panel.classList.toggle(
              'is-active',
              isActive
            );

            if (
              isActive &&
              options.animate !==
                false
            ) {
              panel.classList.remove(
                'is-entering'
              );

              window.requestAnimationFrame(
                runSafely(() => {
                  panel.classList.add(
                    'is-entering'
                  );
                })
              );
            }
          }
        );

        if (
          options.scrollTab !==
          false
        ) {
          const targetLeft =
            targetButton.offsetLeft -
            Math.max(
              0,
              (
                tabList.clientWidth -
                targetButton
                  .offsetWidth
              ) / 2
            );

          tabList.scrollTo({
            left:
              targetLeft,
            behavior:
              window
                .matchMedia(
                  '(prefers-reduced-motion: reduce)'
                )
                .matches
                ? 'auto'
                : 'smooth',
          });
        }

        if (options.focus) {
          targetButton.focus({
            preventScroll: true,
          });
        }

        targetPanel.dispatchEvent(
          new CustomEvent(
            'gvs:product-tab:shown',
            {
              bubbles: true,
              detail: {
                key,
              },
            }
          )
        );

        if (
          options.scrollPanel
        ) {
          scrollPanelIntoView(
            targetPanel
          );
        }

        if (
          options.updateHash !==
            false &&
          hub.dataset
            .updateHash ===
            'true'
        ) {
          const hash =
            normaliseHash(
              targetPanel.dataset
                .hash
            );

          if (hash) {
            history.replaceState(
              null,
              '',
              `#${hash}`
            );
          }
        }
      };

      const safeActivate = (
        key,
        options = {}
      ) => {
        try {
          activate(
            key,
            options
          );
        } catch (error) {
          handleRuntimeError(
            error
          );
        }
      };

      const panelForHash = (
        hashValue
      ) => {
        const hash =
          normaliseHash(
            hashValue
          );

        if (!hash) return null;

        const directPanel =
          availablePanels.find(
            (panel) =>
              normaliseHash(
                panel.dataset
                  .hash
              ) === hash ||
              panel.id ===
                hash
          );

        if (directPanel) {
          return directPanel;
        }

        const target =
          document.getElementById(
            hash
          );

        if (!target) {
          return null;
        }

        return (
          availablePanels.find(
            (panel) =>
              panel.contains(
                target
              )
          ) || null
        );
      };

      buttons.forEach(
        (
          button,
          index
        ) => {
          button.addEventListener(
            'click',
            () =>
              safeActivate(
                button.dataset
                  .gvsTabTarget,
                {
                  scrollPanel:
                    true,
                }
              ),
            {
              signal,
            }
          );

          button.addEventListener(
            'keydown',
            runSafely(
              (event) => {
                let nextIndex =
                  null;

                if (
                  event.key ===
                    'ArrowRight' ||
                  event.key ===
                    'ArrowDown'
                ) {
                  nextIndex =
                    (
                      index + 1
                    ) %
                    buttons.length;
                } else if (
                  event.key ===
                    'ArrowLeft' ||
                  event.key ===
                    'ArrowUp'
                ) {
                  nextIndex =
                    (
                      index -
                      1 +
                      buttons.length
                    ) %
                    buttons.length;
                } else if (
                  event.key ===
                  'Home'
                ) {
                  nextIndex = 0;
                } else if (
                  event.key ===
                  'End'
                ) {
                  nextIndex =
                    buttons.length -
                    1;
                }

                if (
                  nextIndex ===
                  null
                ) {
                  return;
                }

                event.preventDefault();

                safeActivate(
                  buttons[
                    nextIndex
                  ].dataset
                    .gvsTabTarget,
                  {
                    focus: true,
                    scrollPanel:
                      true,
                  }
                );
              }
            ),
            {
              signal,
            }
          );
        }
      );

      document.addEventListener(
        'click',
        runSafely(
          (event) => {
            const link =
              event.target
                .closest?.(
                  'a[href^="#"]'
                );

            if (!link) {
              return;
            }

            const linkedPanel =
              panelForHash(
                link.getAttribute(
                  'href'
                )
              );

            if (linkedPanel) {
              safeActivate(
                linkedPanel.dataset
                  .gvsTabPanel,
                {
                  updateHash:
                    false,
                  animate: false,
                  scrollPanel:
                    true,
                }
              );
            }
          }
        ),
        {
          signal,
        }
      );

      window.addEventListener(
        'hashchange',
        runSafely(() => {
          const hashPanel =
            panelForHash(
              window.location
                .hash
            );

          if (hashPanel) {
            safeActivate(
              hashPanel.dataset
                .gvsTabPanel,
              {
                updateHash:
                  false,
                animate: false,
                scrollPanel:
                  true,
              }
            );
          }
        }),
        {
          signal,
        }
      );

      const initialHashPanel =
        panelForHash(
          window.location.hash
        );

      const initialPanel =
        initialHashPanel ||
        availablePanels[0];

      activate(
        initialPanel.dataset
          .gvsTabPanel,
        {
          updateHash: false,
          animate: false,
          scrollTab: false,
        }
      );

      hub.dataset
        .gvsTabsInitialised =
        'true';

      hub.classList.add(
        'is-ready'
      );

      requestTabListUpdate();

      if (initialHashPanel) {
        window.requestAnimationFrame(
          runSafely(() => {
            const hashTarget =
              document.getElementById(
                normaliseHash(
                  window.location
                    .hash
                )
              );

            (
              hashTarget || hub
            ).scrollIntoView({
              block: 'start',
            });
          })
        );
      }
    } catch (error) {
      failOpenHub(
        hub,
        state,
        error
      );
    }
  };

  const initialiseAll = (
    scope = document
  ) => {
    if (
      scope.matches?.(
        HUB_SELECTOR
      )
    ) {
      initialiseHub(scope);
    }

    scope
      .querySelectorAll?.(
        HUB_SELECTOR
      )
      .forEach(
        initialiseHub
      );
  };

  const cleanupAffectedHubs = (
    scope = document
  ) => {
    for (
      const [
        hub,
        state,
      ] of [
        ...hubStates.entries(),
      ]
    ) {
      const hubIsAffected =
        hub === scope ||
        Boolean(
          scope.contains?.(hub)
        );

      const sourceIsAffected =
        state.movedSources.some(
          (record) =>
            record.wrapper ===
              scope ||
            Boolean(
              scope.contains?.(
                record.wrapper
              )
            )
        );

      if (
        hubIsAffected ||
        sourceIsAffected
      ) {
        cleanupHub(
          hub,
          {
            restoreSources:
              true,
          }
        );
      }
    }
  };

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      () => initialiseAll(),
      {
        once: true,
        signal:
          runtimeController
            .signal,
      }
    );
  } else {
    initialiseAll();
  }

  document.addEventListener(
    'shopify:section:load',
    () => {
      window.requestAnimationFrame(
        () =>
          initialiseAll(
            document
          )
      );
    },
    {
      signal:
        runtimeController.signal,
    }
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      cleanupAffectedHubs(
        event.target ||
          document
      );
    },
    {
      signal:
        runtimeController.signal,
    }
  );

  const destroyRuntime = () => {
    for (
      const hub of [
        ...hubStates.keys(),
      ]
    ) {
      cleanupHub(
        hub,
        {
          restoreSources: true,
        }
      );
    }

    runtimeController.abort();

    if (
      window[RUNTIME_KEY]
        ?.destroy ===
      destroyRuntime
    ) {
      delete window[
        RUNTIME_KEY
      ];
    }
  };

  window[RUNTIME_KEY] = {
    destroy: destroyRuntime,
  };
})();